import i18n, { t } from 'i18next'

import * as S from './styles'

interface MetricMethodologyTooltipProps {
  description: string
  formula?: string
  note?: string
  unavailableNote?: string
  whatChanged?: string
  learnMoreHref?: string
}

/**
 * Primitivo de conteúdo de tooltip de métrica: descrição obrigatória; fórmula,
 * nota, nota de indisponibilidade, caixa "o que mudou" e link "saiba mais"
 * são renderizados quando informados — cada um em seu próprio parágrafo.
 */
export const MetricMethodologyTooltip = ({
  description,
  formula,
  note,
  unavailableNote,
  whatChanged,
  learnMoreHref
}: MetricMethodologyTooltipProps) => (
  <S.Wrapper>
    <p className="description">{description}</p>
    {formula && <p className="formula">{formula}</p>}
    {note && <p className="note">{note}</p>}
    {unavailableNote && (
      <div className="unavailableNote">{unavailableNote}</div>
    )}
    {whatChanged && <div className="whatChanged">{whatChanged}</div>}
    {learnMoreHref && (
      <a
        className="learnMore"
        href={learnMoreHref}
        target="_blank"
        rel="noreferrer"
      >
        {t('c:actions.learnMore')}
      </a>
    )}
  </S.Wrapper>
)

interface MetricTooltipProps {
  /** Chave da métrica em i18n (ex.: 'roas', 'conversion_rate', 'roasClick'). */
  metric: string
  /** Link opcional "saiba mais" (ex.: central de ajuda). */
  learnMoreHref?: string
  /** Valores de interpolação para as chaves i18n (ex.: { date: '22 de jun. de 2026' }). */
  values?: Record<string, unknown>
}

/**
 * Tooltip de métrica dirigido por convenção de chaves i18n:
 * `rm:metrics.<metric>.tooltip.{description,formula,note,whatChanged}`.
 * Apenas `description` é obrigatória; as demais aparecem quando a chave existe.
 * Reutilizável em cabeçalhos de tabela e em cards.
 */
export const MetricTooltip = ({
  metric,
  learnMoreHref,
  values = {}
}: MetricTooltipProps) => {
  const base = `rm:metrics.${metric}.tooltip`
  // Chaves dinâmicas: o `t` tipado não aceita template string, então usamos
  // um alias com assinatura solta.
  const translate = t as unknown as (
    key: string,
    opts?: Record<string, unknown>
  ) => string
  const optional = (suffix: string) =>
    i18n.exists(`${base}.${suffix}`)
      ? translate(`${base}.${suffix}`, values)
      : undefined

  return (
    <MetricMethodologyTooltip
      description={translate(`${base}.description`, values)}
      formula={optional('formula')}
      note={optional('note')}
      unavailableNote={optional('unavailableNote')}
      whatChanged={optional('whatChanged')}
      learnMoreHref={learnMoreHref}
    />
  )
}
